import { Skeleton, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import TableProducts from '/@/components/crop-monitoring/table-products';
import useUnitContext from '/@/core/context/unit';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useProducts } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getEnvironment, getPlantingDate, getRecommendation } from '/@/store/crop-monitoring/selectors';
import { getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import Title from '/@/v3/components/title';
import Alert from '../../bayer/alert';
import styles from '../styles.module.css';

const StepChooseHybrid = ({ selected: fieldId, data, setData, isMobile }) => {
    const units = useUnitContext();

    const environment = useSelector(getEnvironment);
    const plantingDate = useSelector(getPlantingDate);
    const country = useSelector(getSessionCountry);
    const { data: field } = useFieldById({ id: fieldId }, { enabled: !!fieldId });
    const recommendationId = useSelector(getRecommendation);

    const { data: recommendation } = useRecommendationById(recommendationId);
    const cropConfig = { environment: environment, plantingDate, crop: 'corn' };
    if (recommendation && recommendation.crops) cropConfig.cornProducts = recommendation.crops?.[0]?.cornProducts;
    const sessionBrands = useSelector(getSessionBrands);
    const cornBrandCodenames = (sessionBrands.corn || [])
        .map((brand) => brand.codename)
        .filter(Boolean)
        .join(',');

    const productsQuery = useProducts({
        lat: field?.lat,
        lng: field?.lng,
        crop: cropConfig?.crop,
        plantingDate: cropConfig?.plantingDate,
        brandCodename: cornBrandCodenames,
        environment: cropConfig?.environment,
    });

    const filteredProducts = useMemo(() => {
        if (!productsQuery.data) return [];
        if (country.code === 'BR') {
            const products = [...productsQuery.data.featuredProducts, ...productsQuery.data.otherProducts].filter(
                (p) => p.leafCounterCoefficients.length
            );
            return products;
        }
        if (['AR', 'UY'].includes(country.code)) {
            const products = [...productsQuery.data.featuredProducts, ...productsQuery.data.otherProducts];
            return products.concat({ id: null, product: { catalogName: _('Híbrido Genérico') } });
        }
    }, [country.code, productsQuery.data]);

    const showAlert =
        recommendation &&
        !cropConfig.cornProducts?.some((item) => filteredProducts.some((p) => p.id === item.id)) &&
        country.code === 'BR';

    return (
        <>
            {productsQuery.isValidating ? (
                <Stack spacing={1}>
                    {[...Array(5)].map((_, i) => (
                        <Skeleton variant='rectangular' key={i} width={'100%'} height={'30px'} />
                    ))}
                </Stack>
            ) : (
                <Grid container justifyContent='space-between' alignItems='center' sx={isMobile && { pb: 3 }}>
                    <Grid item xs={12} sx={{ my: 2 }}>
                        {showAlert && (
                            <Title className={styles.flexCenter} size='md'>
                                {_(
                                    'No tenemos datos suficientes del híbrido seleccionado en tu recomendación, te sugerimos seleccionar entre los siguientes:'
                                )}
                            </Title>
                        )}
                    </Grid>
                    <Grid item xs={7}>
                        {Boolean(recommendation) && !showAlert && (
                            <Grid container justifyContent='flex-start' alignItems='center' wrap='nowrap' spacing={1}>
                                <Grid item>
                                    <div className={styles.greenSquare} />
                                </Grid>

                                <Grid item>
                                    <Title className={styles.descriptionStep}>
                                        <ReactMarkdown className={styles.descriptionStep}>
                                            {_(
                                                '*Productos seleccionados en la recomendación (Podés elegir uno solo).*'
                                            )}
                                        </ReactMarkdown>
                                    </Title>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>

                    {!!filteredProducts.length && (
                        <Grid item xs={5} sx={{ textAlign: 'end' }}>
                            <Title italic className={styles.descriptionStep}>
                                {_('Rendimiento estimado')} ({units.getUnit('yield', 'corn')})
                            </Title>
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <TableProducts
                            products={filteredProducts}
                            cropConfig={cropConfig}
                            data={data}
                            setData={setData}
                            showIndex={country.code !== 'BR'}
                        />
                    </Grid>
                    {!filteredProducts.length && (
                        <Alert
                            severity='danger'
                            sx={{
                                my: 1,
                            }}
                        >
                            {_(
                                'No hemos encontrado productos para el ambiente seleccionado. Volvé al paso anterior y editá la información configurada.'
                            )}
                        </Alert>
                    )}
                </Grid>
            )}
        </>
    );
};

export default StepChooseHybrid;
